import React, { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  BarChart3, Users, TrendingUp, Phone, Search,
  UserPlus, Loader2, Calendar, Building2, Mail, Briefcase, Download,
  Send, MessageSquare, CheckSquare, Square, X,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { MarketingContactForm } from './MarketingContactForm';
import {
  collection, query, orderBy, onSnapshot,
  doc, updateDoc, addDoc, serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

interface MarketingContact {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
  company_name: string | null;
  phone: string | null;
  email: string | null;
  specialty: string | null;
  conversion_status: string;
  source: string;
  contacted_by: string;
  notes: string | null;
  created_at: string;
}

interface ConversionStats {
  total: number; contacted: number; interested: number;
  meeting_scheduled: number; converted: number;
  not_interested: number; follow_up: number;
}

const ROLE_LABELS: Record<string, string> = {
  broker: 'Broker', buyer: 'Acheteur', seller: 'Vendeur',
  business_lawyer: 'Business Lawyer', immigration_attorney: 'Immigration Attorney',
  lender: 'Lender', accountant: 'Comptable', consultant: 'Consultant',
  investor: 'Investisseur', other: 'Autre',
};

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  contacted: { label: 'Contacté', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  interested: { label: 'Intéressé', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
  meeting_scheduled: { label: 'RDV planifié', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  converted: { label: 'Converti', color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
  not_interested: { label: 'Non intéressé', color: 'bg-red-500/20 text-red-400 border-red-500/30' },
  follow_up: { label: 'À recontacter', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
};

function normalizeContact(id: string, data: any): MarketingContact {
  return {
    id,
    first_name: data.firstName || data.first_name || '',
    last_name: data.lastName || data.last_name || '',
    role: data.role || '',
    company_name: data.companyName || data.company_name || null,
    phone: data.phone || null,
    email: data.email || null,
    specialty: data.specialty || null,
    conversion_status: data.conversionStatus || data.conversion_status || 'contacted',
    source: data.source || '',
    contacted_by: data.contactedBy || data.contacted_by || '',
    notes: data.notes || null,
    created_at: data.createdAt?.toDate?.()?.toISOString() || data.created_at || new Date().toISOString(),
  };
}

export function MarketingDashboard() {
  const { user } = useAuth();
  const [contacts, setContacts] = useState<MarketingContact[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showCampaign, setShowCampaign] = useState(false);

  // ─── Bloc E : Firestore realtime listener ──────────────────────────
  useEffect(() => {
    const unsub = onSnapshot(
      query(collection(db, 'marketingContacts'), orderBy('createdAt', 'desc')),
      (snap) => {
        setContacts(snap.docs.map(d => normalizeContact(d.id, d.data())));
        setLoading(false);
      },
      (err) => {
        console.error('Marketing realtime error:', err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const stats: ConversionStats = useMemo(() => ({
    total: contacts.length,
    contacted: contacts.filter(c => c.conversion_status === 'contacted').length,
    interested: contacts.filter(c => c.conversion_status === 'interested').length,
    meeting_scheduled: contacts.filter(c => c.conversion_status === 'meeting_scheduled').length,
    converted: contacts.filter(c => c.conversion_status === 'converted').length,
    not_interested: contacts.filter(c => c.conversion_status === 'not_interested').length,
    follow_up: contacts.filter(c => c.conversion_status === 'follow_up').length,
  }), [contacts]);

  const conversionRate = stats.total > 0 ? ((stats.converted / stats.total) * 100).toFixed(1) : '0';

  const filtered = contacts.filter(c => {
    if (filterRole !== 'all' && c.role !== filterRole) return false;
    if (filterStatus !== 'all' && c.conversion_status !== filterStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      return c.first_name.toLowerCase().includes(q) || c.last_name.toLowerCase().includes(q) ||
        (c.company_name || '').toLowerCase().includes(q) || (c.phone || '').includes(q) ||
        (c.email || '').toLowerCase().includes(q);
    }
    return true;
  });

  const updateStatus = async (id: string, newStatus: string) => {
    try {
      await updateDoc(doc(db, 'marketingContacts', id), {
        conversionStatus: newStatus,
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAllFiltered = () => {
    const ids = filtered.filter(c => c.phone).map(c => c.id);
    setSelected(new Set(ids));
  };

  const clearSelection = () => setSelected(new Set());

  const exportCSV = () => {
    const headers = ['Prénom','Nom','Rôle','Entreprise','Téléphone','Email','Spécialité','Statut','Source','Notes','Date création'];
    const rows = filtered.map(c => [
      c.first_name, c.last_name, ROLE_LABELS[c.role] || c.role, c.company_name || '',
      c.phone || '', c.email || '', c.specialty || '',
      STATUS_CONFIG[c.conversion_status]?.label || c.conversion_status,
      c.source, c.notes || '', format(new Date(c.created_at), 'yyyy-MM-dd HH:mm'),
    ]);
    const csvContent = [headers, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `marketing-contacts-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const selectedContacts = contacts.filter(c => selected.has(c.id) && c.phone);

  return (
    <div className="h-full flex flex-col">
      <div className="grid grid-cols-4 gap-3 p-4 border-b border-white/10">
        <StatCard icon={<Users className="w-4 h-4" />} label="Total contacts" value={stats.total} color="text-white" />
        <StatCard icon={<TrendingUp className="w-4 h-4" />} label="Taux conversion" value={`${conversionRate}%`} color="text-emerald-400" />
        <StatCard icon={<Phone className="w-4 h-4" />} label="Intéressés" value={stats.interested} color="text-amber-400" />
        <StatCard icon={<Calendar className="w-4 h-4" />} label="RDV planifiés" value={stats.meeting_scheduled} color="text-purple-400" />
      </div>

      <div className="px-4 py-3 border-b border-white/10">
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 className="w-3.5 h-3.5 text-white/40" />
          <span className="text-[11px] font-semibold text-white/50 uppercase tracking-wider">Entonnoir de conversion</span>
        </div>
        <div className="flex items-center gap-1 h-6">
          {(['contacted', 'interested', 'meeting_scheduled', 'converted'] as const).map(status => {
            const count = stats[status];
            const pct = stats.total > 0 ? (count / stats.total) * 100 : 0;
            const cfg = STATUS_CONFIG[status];
            return (
              <div key={status} className="relative group" style={{ flex: Math.max(pct, 5) }}>
                <div className={`h-6 rounded-md ${cfg.color.split(' ')[0]} flex items-center justify-center`}>
                  <span className="text-[10px] font-medium text-white/80">{count}</span>
                </div>
                <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[9px] text-white/30 whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity">
                  {cfg.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-4 py-2 flex items-center gap-2 border-b border-white/10">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
          <Input placeholder="Rechercher un contact..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-8 h-8 text-xs bg-white/5 border-white/10 text-white placeholder:text-white/30" />
        </div>
        <Select value={filterRole} onValueChange={setFilterRole}>
          <SelectTrigger className="w-[150px] h-8 text-[11px] bg-white/5 border-white/10 text-white"><SelectValue placeholder="Tous les rôles" /></SelectTrigger>
          <SelectContent className="bg-gray-900 border-white/10">
            <SelectItem value="all" className="text-white/80 text-[11px]">Tous les rôles</SelectItem>
            {Object.entries(ROLE_LABELS).map(([v, l]) => <SelectItem key={v} value={v} className="text-white/80 text-[11px]">{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-[150px] h-8 text-[11px] bg-white/5 border-white/10 text-white"><SelectValue placeholder="Tous statuts" /></SelectTrigger>
          <SelectContent className="bg-gray-900 border-white/10">
            <SelectItem value="all" className="text-white/80 text-[11px]">Tous statuts</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([v, cfg]) => <SelectItem key={v} value={v} className="text-white/80 text-[11px]">{cfg.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button size="sm" className="h-8 text-xs bg-white/10 hover:bg-white/15 text-white border border-white/10" onClick={exportCSV}>
          <Download className="w-3.5 h-3.5 mr-1" /> CSV
        </Button>
        <Button size="sm" className="h-8 text-xs bg-emerald-600 hover:bg-emerald-500 text-white" onClick={() => setShowForm(true)}>
          <UserPlus className="w-3.5 h-3.5 mr-1" /> Nouveau
        </Button>
      </div>

      {/* Campaign toolbar */}
      <div className="px-4 py-2 flex items-center gap-2 border-b border-white/10 bg-white/[0.02]">
        <Button size="sm" variant="ghost" className="h-7 text-[11px] text-white/60 hover:text-white" onClick={selectAllFiltered}>
          <CheckSquare className="w-3.5 h-3.5 mr-1" /> Tout sélectionner ({filtered.filter(c => c.phone).length})
        </Button>
        {selected.size > 0 && (
          <>
            <Button size="sm" variant="ghost" className="h-7 text-[11px] text-white/60 hover:text-white" onClick={clearSelection}>
              <X className="w-3.5 h-3.5 mr-1" /> Effacer
            </Button>
            <span className="text-[11px] text-white/40">{selectedContacts.length} contact(s) avec téléphone sélectionné(s)</span>
            <div className="ml-auto">
              <Button size="sm" className="h-7 text-[11px] bg-blue-600 hover:bg-blue-500 text-white"
                onClick={() => setShowCampaign(true)} disabled={selectedContacts.length === 0}>
                <Send className="w-3.5 h-3.5 mr-1" /> Campagne SMS
              </Button>
            </div>
          </>
        )}
      </div>

      <ScrollArea className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center p-12"><Loader2 className="w-5 h-5 animate-spin text-white/30" /></div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 text-white/30">
            <Users className="w-8 h-8 mb-2 opacity-50" />
            <p className="text-xs">Aucun contact marketing</p>
            <Button variant="ghost" size="sm" className="mt-2 text-xs text-emerald-400" onClick={() => setShowForm(true)}>
              <UserPlus className="w-3 h-3 mr-1" /> Ajouter un contact
            </Button>
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {filtered.map(contact => {
              const isSel = selected.has(contact.id);
              return (
              <div key={contact.id} className={`px-4 py-3 transition-colors group ${isSel ? 'bg-blue-500/5' : 'hover:bg-white/[0.03]'}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => toggleSelect(contact.id)}
                      disabled={!contact.phone}
                      className="mt-1 text-white/40 hover:text-white disabled:opacity-20 disabled:cursor-not-allowed"
                      title={contact.phone ? 'Sélectionner pour campagne' : 'Pas de téléphone'}>
                      {isSel ? <CheckSquare className="w-4 h-4 text-blue-400" /> : <Square className="w-4 h-4" />}
                    </button>
                    <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-bold text-white/50">{contact.first_name[0]}{contact.last_name[0]}</span>
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-white">{contact.first_name} {contact.last_name}</span>
                        <Badge variant="outline" className="text-[9px] h-4 border-white/10 text-white/50">{ROLE_LABELS[contact.role] || contact.role}</Badge>
                        <Badge variant="outline" className={`text-[9px] h-4 border ${STATUS_CONFIG[contact.conversion_status]?.color || 'text-white/40'}`}>
                          {STATUS_CONFIG[contact.conversion_status]?.label || contact.conversion_status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[11px] text-white/40">
                        {contact.company_name && <span className="flex items-center gap-1"><Building2 className="w-3 h-3" />{contact.company_name}</span>}
                        {contact.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{contact.phone}</span>}
                        {contact.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{contact.email}</span>}
                        {contact.specialty && <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{contact.specialty}</span>}
                      </div>
                      {contact.notes && <p className="text-[11px] text-white/30 mt-1 line-clamp-1">{contact.notes}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <select value={contact.conversion_status} onChange={e => updateStatus(contact.id, e.target.value)}
                      className="h-6 text-[10px] bg-white/5 border border-white/10 text-white/60 rounded px-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                      {Object.entries(STATUS_CONFIG).map(([v, cfg]) => <option key={v} value={v}>{cfg.label}</option>)}
                    </select>
                    <span className="text-[10px] text-white/20">{format(new Date(contact.created_at), 'dd MMM', { locale: fr })}</span>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        )}
      </ScrollArea>

      <MarketingContactForm isOpen={showForm} onClose={() => setShowForm(false)} />

      <SmsCampaignDialog
        isOpen={showCampaign}
        onClose={() => setShowCampaign(false)}
        contacts={selectedContacts}
        userId={user?.id || ''}
        onSent={() => { setShowCampaign(false); clearSelection(); }}
      />
    </div>
  );
}

// ─── Bloc E : SMS Campaign dialog ────────────────────────────────────
function SmsCampaignDialog({
  isOpen, onClose, contacts, userId, onSent,
}: {
  isOpen: boolean;
  onClose: () => void;
  contacts: MarketingContact[];
  userId: string;
  onSent: () => void;
}) {
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState({ sent: 0, failed: 0 });

  const send = async () => {
    if (!message.trim()) { toast.error('Message requis'); return; }
    if (contacts.length === 0) { toast.error('Aucun destinataire'); return; }

    setSending(true);
    setProgress({ sent: 0, failed: 0 });
    const sendSms = httpsCallable(functions, 'sendSmsNotification');
    const campaignName = name.trim() || `Campagne ${format(new Date(), 'yyyy-MM-dd HH:mm')}`;
    let sent = 0, failed = 0;
    const errors: string[] = [];

    for (const c of contacts) {
      try {
        const personalized = message
          .replace(/\{first_name\}/g, c.first_name)
          .replace(/\{last_name\}/g, c.last_name)
          .replace(/\{company\}/g, c.company_name || '');
        await sendSms({ to: c.phone, message: personalized });
        sent++;
      } catch (err: any) {
        failed++;
        errors.push(`${c.phone}: ${err?.message || 'erreur'}`);
        console.error('Campaign SMS error:', err);
      }
      setProgress({ sent, failed });
      // Throttle 250ms between sends to avoid Twilio rate limits
      await new Promise(r => setTimeout(r, 250));
    }

    try {
      await addDoc(collection(db, 'marketingCampaigns'), {
        name: campaignName,
        message,
        recipientCount: contacts.length,
        sentCount: sent,
        failedCount: failed,
        errors: errors.slice(0, 20),
        sentBy: userId,
        contactIds: contacts.map(c => c.id),
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      console.error('Failed to persist campaign:', err);
    }

    setSending(false);
    toast.success(`Campagne envoyée: ${sent} OK, ${failed} échec(s)`);
    setName(''); setMessage(''); setProgress({ sent: 0, failed: 0 });
    onSent();
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && !sending && onClose()}>
      <DialogContent className="bg-[hsl(220,20%,12%)] border-white/10 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <MessageSquare className="w-4 h-4 text-blue-400" /> Campagne SMS — {contacts.length} destinataire(s)
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div>
            <label className="text-[11px] text-white/50">Nom de la campagne (optionnel)</label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Promo Q1 2026"
              className="h-9 text-sm bg-white/5 border-white/10 text-white placeholder:text-white/30 mt-1" disabled={sending} />
          </div>
          <div>
            <label className="text-[11px] text-white/50">Message SMS</label>
            <Textarea value={message} onChange={e => setMessage(e.target.value)}
              placeholder="Bonjour {first_name}, ..."
              className="min-h-[120px] text-sm bg-white/5 border-white/10 text-white placeholder:text-white/30 mt-1" disabled={sending} />
            <div className="flex items-center justify-between mt-1">
              <span className="text-[10px] text-white/30">Variables: {'{first_name}'}, {'{last_name}'}, {'{company}'}</span>
              <span className="text-[10px] text-white/40">{message.length} car.</span>
            </div>
          </div>
          {sending && (
            <div className="bg-white/5 rounded-md p-2 text-[11px] text-white/60 flex items-center gap-2">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Envoi en cours… {progress.sent + progress.failed}/{contacts.length} ({progress.sent} OK, {progress.failed} échec)
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={sending} className="text-white/50 hover:text-white">Annuler</Button>
          <Button size="sm" onClick={send} disabled={sending || !message.trim()} className="bg-blue-600 hover:bg-blue-500 text-white">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Send className="w-3.5 h-3.5 mr-1" />}
            Envoyer à {contacts.length}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: number | string; color: string }) {
  return (
    <div className="bg-white/5 rounded-xl p-3 border border-white/5">
      <div className="flex items-center gap-2 mb-1"><span className="text-white/30">{icon}</span><span className="text-[10px] text-white/40 uppercase tracking-wider">{label}</span></div>
      <span className={`text-xl font-bold ${color}`}>{value}</span>
    </div>
  );
}
